import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, InputAdornment, Stack, TextField } from "@mui/material";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import AndroidRoundedIcon from "@mui/icons-material/AndroidRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { toast } from "react-toastify";
import api from "./Api";
import { hasAnyAuthRole, setAuthSession } from "./auth";
import LoginShell from "./LoginShell";
import PasswordResetDialog from "./PasswordResetDialog";

const LoginUser = () => {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasAnyAuthRole(["user", "admin"])) navigate("/");
  }, [navigate]);

  const handleLogin = async () => {
    if (!userId.trim() || !password) {
      toast.error("Enter user ID and password");
      return;
    }

    try {
      setLoading(true);
      const res = await api.post("/api/auth/login", {
        username: userId.trim(),
        password,
        role: "user",
      });
      setAuthSession(res.data);
      toast.success("Login Successful");
      setTimeout(() => {
        window.location.href = "/";
      }, 600);
    } catch (error) {
      console.error("User login failed:", error);
      toast.error(error.response?.data?.error || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginShell
      eyebrow="Scanner access"
      title="User Login"
      subtitle="Sign in to continue scanning."
      footer={
        <Stack spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AdminPanelSettingsRoundedIcon />}
            onClick={() => navigate("/admin/login")}
            sx={{
              borderColor: "rgba(0, 106, 113, 0.35)",
              color: "#006a71",
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            Admin / Super Admin Login
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={() => setResetOpen(true)}
            sx={{
              color: "#006a71",
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            Forgot password?
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="success"
            startIcon={<AndroidRoundedIcon />}
            href="/downloads/scan-app.apk?v=1.8"
            download
            sx={{ fontWeight: 800, textTransform: "none" }}
          >
            Download Android App
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
      <TextField
        label="User ID"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        fullWidth
        autoComplete="username"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonRoundedIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleLogin()}
        fullWidth
        autoComplete="current-password"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockRoundedIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <Button
        variant="contained"
        onClick={handleLogin}
        disabled={loading}
        fullWidth
        size="large"
        startIcon={<LoginRoundedIcon />}
        sx={{
          py: 1.25,
          fontWeight: 900,
          textTransform: "none",
          background: "linear-gradient(90deg, #006a71, #1976d2)",
          boxShadow: "0 14px 28px rgba(25, 118, 210, 0.28)",
          "&:hover": {
            background: "linear-gradient(90deg, #00545a, #1565c0)",
          },
        }}
      >
        {loading ? "Logging in..." : "Login"}
      </Button>
      </Stack>
      <PasswordResetDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        initialUsername={userId}
      />
    </LoginShell>
  );
};

export default LoginUser;
